import React from 'react';
import { Card, CardTheme, CardHeaderTheme, CardBorderRadius, CardBorderStyle, Button, ButtonTheme, useWaitOverlayStore } from '@apply/components';
import { OldPasswordTextBox } from './components/OldPasswordTextBox';
import { NewPasswordTextBox } from './components/NewPasswordTextBox';
import { ConfirmNewPasswordTextBox } from './components/ConfirmNewPasswordTextBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey } from '@fortawesome/free-solid-svg-icons';
import { handleAdminSuccess, handleAdminError } from '../../../../../common/SharedFunctions';
import { BfiApi } from '@apply/api';
import { PasswordResetDirections } from './components/PasswordResetDirections';
import { useBrokerResetPasswordStore } from './state/useBrokerResetPasswordStore';
import './scss/_brokerPasswordResetPanel.scss';
import { useBrokerResetPasswordActions } from './queries/useBrokerResetPasswordActions';

export const PasswordResetPanel: React.FC = () => {
    const passwordResetInfo = useBrokerResetPasswordStore((s) => s.passwordResetInfo);
    const passwordResetActions = useBrokerResetPasswordActions();
    const requiredFieldLength = 8;
    const fieldsValid =
        passwordResetInfo.password.length >= requiredFieldLength &&
        passwordResetInfo.newPassword.length >= requiredFieldLength &&
        passwordResetInfo.confirmNewPassword.length >= requiredFieldLength &&
        passwordResetInfo.newPassword === passwordResetInfo.confirmNewPassword;

    const [passwordResetResult, setPasswordResetResult] = React.useState<BfiApi.IChangePasswordResult>();

    const handleSubmit = async (event: React.MouseEvent) => {
        event.preventDefault();
        setPasswordResetResult(undefined);

        if (passwordResetInfo.password.length === 0) {
            handleAdminError(new Error('Invalid password'), 'Enter a valid password');
            return;
        }

        const { setWaiting } = useWaitOverlayStore.getState();
        try {
            setWaiting(true, 'Updating password...');
            const result = await passwordResetActions.saveAsync();
            if (!result.ok) {
                handleAdminError(result.error, 'An unexpected error has occurred! Please try again.');
                return;
            }

            const response = result.data;
            if (response && response.statusCode !== 200) {
                const apiResult = response.result;

                if (apiResult?.errors?.length > 0) {
                    handleAdminError(
                        new Error('IApiChangePasswordResponse returned errors'),
                        `Encountered an error - ${apiResult.errors}`
                    );
                } else {
                    handleAdminError(new Error('Password policy failure occurred'), 'An unexpected error has occurred');
                }

                return;
            }

            if (!response?.result) {
                handleAdminError(new Error('No response.result returned'), 'An unexpected error occurred. Please try again.');
                return;
            }

            const apiResult = response.result as BfiApi.IApiChangePasswordResponse;
            if (apiResult.result?.succeeded) {
                setPasswordResetResult(apiResult.result);
                handleAdminSuccess('Password updated!');
                return;
            }

            if (apiResult.errors?.length > 0) {
                handleAdminError(
                    new Error('IApiChangePasswordResponse returned errors'),
                    `Encountered an error - ${apiResult.errors}`
                );
                return;
            }

            // Password policy failure
            setPasswordResetResult(apiResult.result);
        } catch (ex) {
            handleAdminError(ex as Error, 'An unexpected error has occurred! Please try again.');
        } finally {
            setWaiting(false);
        }
    };

    const cardHeader = () => {
        return (
            <>
                <div className='brokerSettingsCardHeader'>
                    <FontAwesomeIcon
                        icon={faKey}
                        className='icon'
                        style={{ paddingRight: '10px' }}
                    />
                    <h4>Password</h4>
                </div>
            </>
        );
    };

    return (
        <>
            <div className='brokerSettingsBrokerPasswordReset'>
                <Card
                    theme={CardTheme.White}
                    headerTheme={CardHeaderTheme.Gray}
                    borderStyle={CardBorderStyle.SolidDarkerBorder}
                    borderRadius={CardBorderRadius.Small}
                    header={cardHeader()}
                    collapsible={true}
                >
                    <PasswordResetDirections failureResult={passwordResetResult} />
                    <div className='brokerSettingsPasswordResetFields'>
                        <OldPasswordTextBox required={true} />
                        <NewPasswordTextBox required={true} />
                        <ConfirmNewPasswordTextBox required={true} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            id='updateBrokerSettingsPasswordButton'
                            theme={ButtonTheme.Primary}
                            disabled={!fieldsValid}
                            width='200px'
                            height='42px'
                            handleClick={(event) => handleSubmit(event)}
                        >
                            Update Password
                        </Button>
                    </div>
                </Card>
            </div>
        </>
    );
};
